import { useRef, useState } from "react"
import { ChevronDown, Loader2 } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useMicrophones } from "@/lib/audio-input"
import { useMicrophoneId } from "@/lib/preferences"
import { cn } from "@/lib/utils"

const SYSTEM_DEFAULT = "system"

export function MicrophoneMenu({
  onSelect,
  activeLabel,
  disabled,
  className,
}: {
  onSelect: (deviceId: string | null) => Promise<void>
  activeLabel?: string
  disabled?: boolean
  className?: string
}) {
  const [selected, setSelected] = useMicrophoneId()
  const [changing, setChanging] = useState(false)
  const changingRef = useRef(false)
  const { microphones, labelsHidden, supported, requestAccess, refresh } =
    useMicrophones()
  const known = microphones.some((mic) => mic.deviceId === selected)

  const choose = async (value: string) => {
    if (changingRef.current) return
    changingRef.current = true
    setChanging(true)
    const deviceId = value === SYSTEM_DEFAULT ? null : value
    try {
      await onSelect(deviceId)
      setSelected(deviceId)
    } catch {
      toast.error(
        "Mikrofonwechsel fehlgeschlagen. Die bisherige Auswahl bleibt aktiv."
      )
    } finally {
      changingRef.current = false
      setChanging(false)
    }
  }

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) void refresh()
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled || changing}
          aria-label={
            changing ? "Mikrofon wird gewechselt" : "Mikrofon auswählen"
          }
          title={
            activeLabel ? `Mikrofon: ${activeLabel}` : "Mikrofon auswählen"
          }
          className={cn(
            "h-9 w-6 shrink-0 rounded-md text-muted-foreground",
            className
          )}
        >
          {changing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="end"
        className="w-72 max-w-[calc(100vw-2rem)]"
      >
        <DropdownMenuLabel>Mikrofon auswählen</DropdownMenuLabel>
        {activeLabel && (
          <p className="px-2 pb-2 text-xs text-muted-foreground">
            Aktiv: {activeLabel}
          </p>
        )}
        <DropdownMenuSeparator />
        {supported ? (
          <>
            <DropdownMenuRadioGroup
              value={selected ?? SYSTEM_DEFAULT}
              onValueChange={(value) => void choose(value)}
            >
              <DropdownMenuRadioItem value={SYSTEM_DEFAULT}>
                Systemstandard
              </DropdownMenuRadioItem>
              {microphones
                .filter((mic) => mic.deviceId)
                .map((mic) => (
                  <DropdownMenuRadioItem
                    key={mic.deviceId}
                    value={mic.deviceId}
                  >
                    {mic.label}
                  </DropdownMenuRadioItem>
                ))}
              {selected && !known && (
                <DropdownMenuRadioItem value={selected} disabled>
                  Zuletzt gewählt (nicht verbunden)
                </DropdownMenuRadioItem>
              )}
            </DropdownMenuRadioGroup>
            {(labelsHidden || !microphones.length) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => void requestAccess()}>
                  Mikrofonzugriff erlauben
                </DropdownMenuItem>
                <p className="px-2 py-1 text-xs text-muted-foreground">
                  Erlaube den Zugriff im Browser, um die Gerätenamen anzuzeigen.
                </p>
              </>
            )}
          </>
        ) : (
          <p className="px-2 py-1 text-xs text-muted-foreground">
            Die Mikrofonauswahl ist in diesem Browser nicht verfügbar.
          </p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
